import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const ThemeContext = createContext(null);

const DEFAULT = {
  id: "main",
  current_theme: "theme_1",
  social: {},
  val_avatar: "",
  val_name: "Val",
  val_welcome_message: "¡Hola! Soy Val 💕",
  hero_title: "Tu Riviera Maya,\ndiseñada a tu medida",
  hero_subtitle: "Experiencias curadas de lujo.",
  contact_email: "",
  contact_phone: "",
  contact_whatsapp: "",
};

const CACHE_KEY = "benova_settings_cache_v1";

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.data) return parsed.data;
  } catch (e) { /* ignore */ }
  return null;
}

function writeCache(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() }));
  } catch (e) { /* ignore */ }
}

export function ThemeProvider({ children }) {
  // Initialise from localStorage so 2nd+ visits render the user's branding instantly.
  const cached = readCache();
  const [settings, setSettings] = useState(cached ? { ...DEFAULT, ...cached } : DEFAULT);
  const [loaded, setLoaded] = useState(!!cached);

  // Apply theme attribute synchronously on the very first paint
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", (cached?.current_theme) || DEFAULT.current_theme);
    // eslint-disable-next-line
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/settings/public");
      setSettings({ ...DEFAULT, ...res.data });
      writeCache(res.data);
    } catch (e) {
      if (!cached) setSettings(DEFAULT);
    } finally {
      setLoaded(true);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = settings.current_theme || "theme_1";
    document.documentElement.setAttribute("data-theme", t);
  }, [settings.current_theme]);

  // Preload hero image so it's in cache before the user scrolls
  useEffect(() => {
    if (settings.hero_image) {
      const img = new Image();
      img.src = settings.hero_image;
    }
  }, [settings.hero_image]);

  return (
    <ThemeContext.Provider value={{ settings, setSettings, reload: load, loaded }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useSettings() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useSettings outside provider");
  return ctx;
}
